import { c as _c } from "react/compiler-runtime";
import './radio-group-buttons.scss';
import React, { PropsWithChildren, useEffect } from 'react';
import { useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupState, useRadioGroupState } from 'react-stately';
import { cn } from '../../utils/helpers.ts';
import { ButtonGroupInputType, ButtonGroupItem } from '../button-group-item/button-group-item.tsx';
const RadioContext = React.createContext<RadioGroupState | null>(null);
type RadioGroupButtonsProps<TValue> = PropsWithChildren & {
  errorMessage?: string;
  label?: string;
  defaultValue?: TValue;
  listClassName?: string;
  onChange: (selectedValue: TValue) => void;
};

// Looks like VSCode has some issues with an arrow function here without adding some verbose type assertions.
// eslint-disable-next-line prefer-arrow-functions/prefer-arrow-functions
export function RadioGroupButtons(props) {
  const $ = _c(29);
  let listClassName;
  let onChange;
  let rest;
  if ($[0] !== props) {
    ({
      listClassName,
      onChange,
      ...rest
    } = props);
    $[0] = props;
    $[1] = listClassName;
    $[2] = onChange;
    $[3] = rest;
  } else {
    listClassName = $[1];
    onChange = $[2];
    rest = $[3];
  }
  const {
    children,
    errorMessage,
    label
  } = rest;
  const state = useRadioGroupState(rest);
  const {
    radioGroupProps,
    labelProps,
    errorMessageProps
  } = useRadioGroup(rest, state);
  const {
    selectedValue
  } = state;
  let t0;
  if ($[4] !== onChange || $[5] !== selectedValue) {
    t0 = () => {
      onChange(selectedValue as TValue);
    };
    $[4] = onChange;
    $[5] = selectedValue;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== selectedValue) {
    t1 = [selectedValue];
    $[7] = selectedValue;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
  let t2;
  if ($[9] !== label || $[10] !== labelProps) {
    t2 = label && <p className="RadioGroupButtons__label" {...labelProps}>{label}</p>;
    $[9] = label;
    $[10] = labelProps;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== listClassName) {
    t3 = cn("RadioGroupButtons__list gap-comfy-xs my-comfy-lg", listClassName);
    $[12] = listClassName;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[14] = children;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== state || $[18] !== t4) {
    t5 = <RadioContext.Provider value={state}>{t4}</RadioContext.Provider>;
    $[17] = state;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== errorMessage || $[21] !== errorMessageProps || $[22] !== state.isInvalid) {
    t6 = errorMessage && state.isInvalid && <div {...errorMessageProps} style={{
      color: "red",
      fontSize: 12
    }}>{errorMessage}</div>;
    $[20] = errorMessage;
    $[21] = errorMessageProps;
    $[22] = state.isInvalid;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== radioGroupProps || $[25] !== t2 || $[26] !== t5 || $[27] !== t6) {
    t7 = <div {...radioGroupProps} className="RadioGroupButtons">{t2}{t5}{t6}</div>;
    $[24] = radioGroupProps;
    $[25] = t2;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}
export type RadioGroupButtonsItemProps = PropsWithChildren<{
  value: string;
  id: string;
}>;
export const RadioGroupButtonsItem: React.FC<RadioGroupButtonsItemProps> = ({
  id,
  children,
  value
}) => {
  return <ButtonGroupItem<RadioGroupState> useItemHook={useRadio} value={value} id={id} stateContext={RadioContext} inputVariant={ButtonGroupInputType.Radio}>
      {children}
    </ButtonGroupItem>;
};